type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

type AcquisitionContext = {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  referrer: string;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: any[]) => void;
    hj?: (...args: any[]) => void;
    _hjSettings?: { hjid: string | number; hjsv: string | number };
    analyticsEnabled?: string;
    gaMeasurementId?: string;
    hotjarSiteId?: string;
    hotjarVersion?: string;
  }
}

const anonymousIdKey = "cs.analytics.anonymousId";
const sessionIdKey = "cs.analytics.sessionId";
const acquisitionContextKey = "cs.analytics.acquisitionContext";
const identifiedUserKey = "cs.analytics.userId";

let initialized = false;

function isAnalyticsEnabled(): boolean {
  return String(window.analyticsEnabled || "").toLowerCase() === "true";
}

function getMeasurementId(): string {
  return String(window.gaMeasurementId || "").trim();
}

function getHotjarSiteId(): string {
  return String(window.hotjarSiteId || "").trim();
}

function getHotjarVersion(): string {
  return String(window.hotjarVersion || "").trim();
}

function generateId(prefix: string): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function ensureAnonymousId(): string {
  const existing = window.localStorage.getItem(anonymousIdKey);
  if (existing) {
    return existing;
  }

  const value = generateId("anon");
  window.localStorage.setItem(anonymousIdKey, value);
  return value;
}

function ensureSessionId(): string {
  const existing = window.sessionStorage.getItem(sessionIdKey);
  if (existing) {
    return existing;
  }

  const value = generateId("session");
  window.sessionStorage.setItem(sessionIdKey, value);
  return value;
}

function readAcquisitionContext(): AcquisitionContext {
  const existing = window.sessionStorage.getItem(acquisitionContextKey);
  if (existing) {
    try {
      return JSON.parse(existing) as AcquisitionContext;
    } catch (error) {
      console.log(error);
    }
  }

  const params = new URLSearchParams(window.location.search);
  const acquisitionContext: AcquisitionContext = {
    utmSource: params.get("utm_source") || "",
    utmMedium: params.get("utm_medium") || "",
    utmCampaign: params.get("utm_campaign") || "",
    referrer: document.referrer || "",
  };

  window.sessionStorage.setItem(acquisitionContextKey, JSON.stringify(acquisitionContext));
  return acquisitionContext;
}

function getRoutePath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function sanitizeEventName(eventKey: string): string {
  return eventKey
    .trim()
    .toLowerCase()
    .replace(/[:\s/]+/g, "_")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "") || "clipsymphony_event";
}

function ensureGoogleAnalytics(): void {
  const measurementId = getMeasurementId();
  if (!isAnalyticsEnabled() || !measurementId) {
    return;
  }

  if (!document.querySelector(`script[data-cs-ga="${measurementId}"]`)) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.dataset.csGa = measurementId;
    document.head.appendChild(script);
  }

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: any[]) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(args);
    };
  }

  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false });
}

function ensureHotjar(): void {
  const siteId = getHotjarSiteId();
  const version = getHotjarVersion();
  if (!isAnalyticsEnabled() || !siteId || !version) {
    return;
  }

  if (document.querySelector(`script[data-cs-hotjar="${siteId}"]`)) {
    return;
  }

  window.hj = window.hj || function hotjarProxy(...args: any[]) {
    (window.hj as any).q = (window.hj as any).q || [];
    (window.hj as any).q.push(args);
  };
  window._hjSettings = { hjid: siteId, hjsv: version };

  const script = document.createElement("script");
  script.async = true;
  script.dataset.csHotjar = siteId;
  script.src = `https://static.hotjar.com/c/hotjar-${siteId}.js?sv=${version}`;
  document.head.appendChild(script);
}

function flattenProperties(properties: AnalyticsProperties): Record<string, string | number | boolean> {
  return Object.entries(properties).reduce((result, [key, value]) => {
    if (value === undefined || value === null) {
      return result;
    }

    result[key] = value;
    return result;
  }, {} as Record<string, string | number | boolean>);
}

function parseUserIdFromStoredToken(): string {
  try {
    const token = window.localStorage.getItem("token");
    if (!token) {
      return "";
    }

    const payload = token.split(".")[1];
    if (!payload) {
      return "";
    }

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((character) => `%${(`00${character.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );
    const parsed = JSON.parse(jsonPayload);
    return parsed.id || parsed.userId || parsed.sub || "";
  } catch (error) {
    console.log(error);
    return "";
  }
}

export function initAnalytics(clientSurface: string): void {
  ensureAnonymousId();
  ensureSessionId();
  readAcquisitionContext();
  ensureGoogleAnalytics();
  ensureHotjar();

  const userId = parseUserIdFromStoredToken();
  if (userId) {
    identifyAnalyticsUser(clientSurface, userId);
  }

  if (!initialized) {
    trackPageView(clientSurface);
    initialized = true;
  }
}

export function identifyAnalyticsUser(clientSurface: string, userId: string): void {
  if (!userId) {
    return;
  }

  window.sessionStorage.setItem(identifiedUserKey, userId);
  if (window.gtag && isAnalyticsEnabled()) {
    window.gtag("set", "user_properties", {
      clipsymphony_user_id: userId,
      source_surface: clientSurface,
    });
  }
}

export function trackIntentEvent(eventKey: string, clientSurface: string, properties: AnalyticsProperties = {}): void {
  if (!isAnalyticsEnabled() || !window.gtag) {
    return;
  }

  const acquisitionContext = readAcquisitionContext();
  const params = flattenProperties({
    event_key: eventKey,
    source_surface: clientSurface,
    route_path: getRoutePath(),
    anonymous_id: ensureAnonymousId(),
    session_id: ensureSessionId(),
    user_id: window.sessionStorage.getItem(identifiedUserKey) || "",
    utm_source: acquisitionContext.utmSource,
    utm_medium: acquisitionContext.utmMedium,
    utm_campaign: acquisitionContext.utmCampaign,
    referrer: acquisitionContext.referrer,
    ...properties,
  });

  window.gtag("event", sanitizeEventName(eventKey), params);
}

export function trackPageView(clientSurface: string): void {
  const measurementId = getMeasurementId();
  if (window.gtag && isAnalyticsEnabled() && measurementId) {
    window.gtag("event", "page_view", {
      page_path: getRoutePath(),
      page_title: document.title,
      page_location: window.location.href,
      source_surface: clientSurface,
    });
  }

  trackIntentEvent("SITE:PAGE_VIEWED", clientSurface, {
    page_path: getRoutePath(),
    page_title: document.title,
  });
}

export function trackRouteView(clientSurface: string, routePath = getRoutePath()): void {
  const measurementId = getMeasurementId();
  if (window.gtag && isAnalyticsEnabled() && measurementId) {
    window.gtag("event", "page_view", {
      page_path: routePath,
      page_title: document.title,
      page_location: window.location.href,
      source_surface: clientSurface,
    });
  }

  trackIntentEvent("SITE:ROUTE_VIEWED", clientSurface, {
    page_path: routePath,
  });
}

export function trackWebVital(clientSurface: string, metric: any): void {
  if (!metric) {
    return;
  }

  trackIntentEvent("SITE:WEB_VITAL_RECORDED", clientSurface, {
    metric_name: metric.name,
    metric_value: typeof metric.value === "number" ? Number(metric.value.toFixed(2)) : metric.value,
    metric_rating: metric.rating,
    metric_id: metric.id,
  });
}

export function buildAnalyticsHeaders(headers: Record<string, string> = {}, clientSurface = ""): Record<string, string> {
  const acquisitionContext = readAcquisitionContext();
  return {
    ...headers,
    "x-cs-session-id": ensureSessionId(),
    "x-cs-anonymous-id": ensureAnonymousId(),
    "x-cs-client-surface": clientSurface,
    "x-cs-route": getRoutePath(),
    "x-cs-acquisition-context": JSON.stringify(acquisitionContext),
  };
}
